import { addBreadcrumb, captureMessage, withScope } from '@sentry/react'
import { useCallback, useEffect, useRef } from 'react'
import type React from 'react'

/**
 * Sentry diagnostics for "provider can't add an ICD-10 code" (QMD-4885).
 *
 * Some providers click a search result and nothing is added, and it never reproduced locally. The
 * prime suspect (a press held past SearchAutocomplete's 150ms blur timer closes the list before the
 * click lands) is fixed in AddICD10CodesModal, but these signals stay so that any other cause — rows
 * replaced mid-press, a failed search, a save that fails silently — shows up in Sentry tagged by
 * `icd10_failure` instead of as a support ticket.
 *
 * PHI: an ICD-10 code on a visit is health information. Nothing here may carry codes, descriptions,
 * search text, VisitId or PatientId — only counts, lengths, timings, indexes, booleans and HTTP
 * status. `SafeData` enforces that: the only strings it accepts are the fixed enums below. The events
 * this module captures also drop Sentry's automatic breadcrumbs, whose request URLs carry the search
 * text and the VisitId.
 */

type PointerKind = 'mouse' | 'pen' | 'touch' | 'unknown'
type SubmitSource = 'modal' | 'chip'
type SafeData = Record<string, number | boolean | null | PointerKind | SubmitSource>

export type Icd10FailureKind =
  | 'row-removed-before-click'
  | 'results-replaced-mid-press'
  | 'released-on-row-without-select'
  | 'list-closed-during-scroll'
  | 'search-failed'
  | 'submit-failed'
  | 'submit-skipped-no-visit'

/** How long after release a click may still arrive (touch dispatches its compatibility click late). */
const SELECT_GRACE_MS = 400

export function icd10Breadcrumb(message: string, data: SafeData) {
  addBreadcrumb({ category: 'icd10', level: 'info', message, data })
}

export function reportIcd10Failure(kind: Icd10FailureKind, data: SafeData) {
  addBreadcrumb({ category: 'icd10', level: 'warning', message: kind, data })
  withScope((scope) => {
    // Keep only this module's trail: fetch/xhr breadcrumbs hold URLs like `icdcodes?SearchValue=…`
    // and `visits/<VisitId>/icdcodes`.
    scope.addEventProcessor((event) => {
      if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.filter((b) => b.category === 'icd10')
      return event
    })
    captureMessage(`icd10: ${kind}`, { level: 'warning', tags: { icd10_failure: kind }, extra: data })
  })
}

/** The HTTP status of an Orval `AppError`, and nothing else from it — its message can echo the request. */
export function errorStatus(error: unknown): number | null {
  if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
    return error.status
  }
  return null
}

const toPointerKind = (pointerType: string): PointerKind =>
  pointerType === 'mouse' || pointerType === 'pen' || pointerType === 'touch' ? pointerType : 'unknown'

type Press = {
  startedAt: number
  /** Index of the pressed result row, or null when the press was on the results scroller itself. */
  index: number | null
  pointerType: PointerKind
  resultCount: number
  resultsVersion: number
  row: Element | null
  listbox: Element | null
  selected: boolean
}

/**
 * Per-mount diagnostics for the add modal. `reportOnce` captures each failure kind at most once per
 * mount (repeats become breadcrumbs), so one confused provider or one outage can't flood Sentry.
 *
 * It also watches presses on the search results and reports the ones that end without selecting a code.
 *
 * Attach `onPointerDownCapture` to an element wrapping the SearchAutocomplete (it only observes; it
 * never prevents anything), mark each result row with `data-icd-result-index`, and call
 * `noteSelected` from the select handler. A row press is judged at release: whether the row and list
 * were still mounted and whether the results changed in between are read synchronously, and whether
 * a selection followed is checked `SELECT_GRACE_MS` later.
 */
export function useIcd10Diagnostics(results: readonly unknown[]) {
  const pressRef = useRef<Press | null>(null)
  const resultsVersionRef = useRef(0)
  const reportedRef = useRef(new Set<Icd10FailureKind>())
  const releaseCleanupRef = useRef<(() => void) | null>(null)
  const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(() => {
    resultsVersionRef.current += 1
  }, [results])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      releaseCleanupRef.current?.()
      timers.forEach(clearTimeout)
    }
  }, [])

  const reportOnce = useCallback((kind: Icd10FailureKind, data: SafeData) => {
    if (reportedRef.current.has(kind)) {
      addBreadcrumb({ category: 'icd10', level: 'warning', message: kind, data })
      return
    }
    reportedRef.current.add(kind)
    reportIcd10Failure(kind, data)
  }, [])

  const handleRelease = useCallback(
    (e: PointerEvent) => {
      releaseCleanupRef.current?.()
      const press = pressRef.current
      if (!press) return
      const releasedAt = performance.now()
      const rowConnected = press.row?.isConnected ?? false
      const listConnected = press.listbox?.isConnected ?? false
      const resultsChanged = resultsVersionRef.current !== press.resultsVersion
      const releasedOnRow = press.row !== null && e.target instanceof Node && press.row.contains(e.target)
      const data: SafeData = {
        index: press.index,
        resultCount: press.resultCount,
        pointerType: press.pointerType,
        pressMs: Math.round(releasedAt - press.startedAt),
        cancelled: e.type === 'pointercancel',
        rowConnected,
        listConnected,
        resultsChanged,
      }

      const timer = setTimeout(() => {
        timersRef.current.delete(timer)
        if (pressRef.current !== press) return
        pressRef.current = null
        if (press.selected) return

        if (press.index === null) {
          if (!listConnected) reportOnce('list-closed-during-scroll', data)
          return
        }
        if (!rowConnected) {
          reportOnce(resultsChanged ? 'results-replaced-mid-press' : 'row-removed-before-click', data)
        } else if (releasedOnRow && e.type === 'pointerup') {
          reportOnce('released-on-row-without-select', data)
        } else {
          icd10Breadcrumb('result press released off the row', data)
        }
      }, SELECT_GRACE_MS)
      timersRef.current.add(timer)
    },
    [reportOnce],
  )

  const onPointerDownCapture = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!(e.target instanceof Element)) return
      const row = e.target.closest('[data-icd-result-index]')
      const listbox = e.target.closest('[role="listbox"]')
      // Only rows and the bare scroller (its scrollbar) — presses on the input are not selections.
      if (!row && (!listbox || e.target !== listbox)) return

      const index = row ? Number(row.getAttribute('data-icd-result-index')) : null
      const press: Press = {
        startedAt: performance.now(),
        index,
        pointerType: toPointerKind(e.pointerType),
        resultCount: results.length,
        resultsVersion: resultsVersionRef.current,
        row,
        listbox,
        selected: false,
      }
      pressRef.current = press
      icd10Breadcrumb(row ? 'result press' : 'results scroller press', {
        index,
        resultCount: press.resultCount,
        pointerType: press.pointerType,
      })

      releaseCleanupRef.current?.()
      document.addEventListener('pointerup', handleRelease, true)
      document.addEventListener('pointercancel', handleRelease, true)
      releaseCleanupRef.current = () => {
        document.removeEventListener('pointerup', handleRelease, true)
        document.removeEventListener('pointercancel', handleRelease, true)
        releaseCleanupRef.current = null
      }
    },
    [results.length, handleRelease],
  )

  const noteSelected = useCallback((hasIcdCodeId: boolean) => {
    const press = pressRef.current
    if (press) press.selected = true
    icd10Breadcrumb('result selected', {
      index: press?.index ?? null,
      pressMs: press ? Math.round(performance.now() - press.startedAt) : null,
      hasIcdCodeId,
    })
  }, [])

  return { onPointerDownCapture, noteSelected, reportOnce }
}
